/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import type { EventBus } from "../../event-bus/event-bus"
import { EventBusEvent } from "../../event-bus/event-bus"
import type { Result } from "../../network/http-client"
import type { ViewModel } from "../../viewmodel/viewmodel"

import type { SessionService } from "./session-service"
import type { SessionStore } from "./session-store"
import type { CurrentUser, LoginFlowResult } from "./session-types"

export class SessionViewModel implements ViewModel {
    name: string = this.constructor.name

    constructor(
        private readonly store: SessionStore,
        private readonly service: SessionService,
        private readonly eventBus: EventBus
    ) {}

    get currentUser(): CurrentUser | null {
        return this.store.currentUser
    }

    get hasUser(): boolean {
        return this.store.currentUser !== null
    }

    get favApps(): number[] {
        return this.store.favAppsArray
    }

    get enabledApps(): number[] {
        return this.store.enabledAppsArray
    }

    isFavApp(appId: number | null): boolean {
        return this.store.isFavApp(appId)
    }

    toggleFavApp(appId: number | null): void {
        this.store.toggleFavApp(appId)
        // persist
        void this.updateUserPreferences()
    }

    isEnabledApp(appId: number | null): boolean {
        return this.store.isEnabledApp(appId)
    }

    toggleEnabledApp(appId: number | null): void {
        this.store.toggleEnabledApp(appId)
        this.eventBus.emit(EventBusEvent.EnabledAppsChanged, this.enabledApps)
        // persist
        void this.updateUserPreferences()
    }

    get currentUserOrgId(): number | undefined {
        return this.store.currentUser?.organizationId
    }

    async login(username: string, password: string, totp: string): Promise<LoginFlowResult> {
        return await this.service.login(username, password, totp)
    }

    async logout(): Promise<void> {
        await this.service.logout()
    }

    async fetchXSRFToken(): Promise<void> {
        return await this.service.fetchXSRFToken()
    }

    async getCurrentUser(suppressErrorLogging: boolean = false): Promise<Result<CurrentUser>> {
        const result = await this.service.getCurrentUser(suppressErrorLogging)
        if (result.success && result.data) {
            this.eventBus.emit(EventBusEvent.CurrentUserLoaded, result.data)
        }
        return result
    }

    async changeUserPassword(userId: number, oldPassword: string, newPassword: string): Promise<Result<void>> {
        return await this.service.changeUserPassword(userId, oldPassword, newPassword)
    }

    async resetPassword(token: string, password: string): Promise<Result<void>> {
        return await this.service.resetPassword(token, password)
    }

    private async updateUserPreferences(): Promise<Result<void>> {
        return await this.service.updateCurrentUser({
            favApps: this.store.favAppsArray,
            enabledApps: this.store.enabledAppsArray
        })
    }
}
